import {Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {MotiDataSource, Summary} from 'src/moti/models/moti-data-source';
import {DataSourceService} from 'src/moti/services/data-source.service';
import {ButtonExtendedComponent} from '../button-extended/button-extended.component';
import {ProgressBarComponent} from '../progress-bar/progress-bar.component';


@Component({
  selector: 'moti-summary-loader',
  templateUrl: './summary-loader.component.html',
  styleUrl: './summary-loader.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonExtendedComponent,
    ProgressBarComponent,
  ],
})
export class SummaryLoaderComponent implements OnInit, OnDestroy {
  @Input() dataSource: MotiDataSource;
  @Input() expanded: boolean = false;

  startingSummaryGeneration: boolean = false;

  /**
   * @see checkSummaryProgress
   * @private
   */
  private timeout: number;


  constructor(
    private dataSourceSvc: DataSourceService,
  ) {
  }


  ngOnInit() {
    if (this.isGeneratingSummary) {
      this.checkSummaryProgress();
    }
  }


  ngOnDestroy() {
    clearTimeout(this.timeout);
  }


  get summary(): Summary {
    return this.dataSource?.summary;
  }


  get isGeneratingSummary(): boolean {
    const source = this.dataSource;

    return this.startingSummaryGeneration ||
      (source.summary?.content?.text === null && !this.isBeforeNow(source.summary?.retryAfter));
  }


  /**
   * This is for uploads that didn't automatically get summarized for whatever reason
   */
  generateSummary() {
    this.startingSummaryGeneration = true;

    this.dataSourceSvc.getUploadSummary$(this.dataSource.id).subscribe({
      next: summary => {
        this.dataSource.summary = summary;
        this.startingSummaryGeneration = false;
        this.checkSummaryProgress();
      },
      error: () => {
        this.startingSummaryGeneration = false;
      },
    });
  }


  /**
   * If any of the sources rendered contained a summary presently in generating status,
   * this method will be called every 5 seconds until all such loading spinners are no
   * longer spinning, or until the timeout is canceled.
   *
   * @private
   */
  private checkSummaryProgress() {
    clearTimeout(this.timeout);

    this.timeout = window.setTimeout(() => {
      return this.dataSourceSvc.getUploadSummary$(this.dataSource.id).subscribe({
        next: summary => {
          this.dataSource.summary = summary;

          if (this.isGeneratingSummary) {
            this.checkSummaryProgress();
          }
        },
      });
    }, 5000);
  }


  protected isBeforeNow(timestamp: string): boolean {
    const date = new Date(timestamp);
    const now = new Date();

    return date < now;
  }
}
